'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { Settings, Users, Shield, Sliders, Save, Plus, Trash2, Edit2, Check, X, Globe, Bell, Lock, Database, Clock, ToggleLeft, ToggleRight, ChevronDown, AlertCircle,  } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


// ─── Types ───────────────────────────────────────────────────────────────────

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  status: 'active' | 'inactive' | 'pending';
  lastLogin: string;
  avatar: string;
}

interface Role {
  id: string;
  name: string;
  description: string;
  color: string;
  permissions: string[];
  userCount: number;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const mockUsers: User[] = [
  { id: 'u1', name: 'Ryan Ashford', email: 'ryan@arrowsgroups.com', role: 'Super Admin', status: 'active', lastLogin: '2 min ago', avatar: 'RA' },
  { id: 'u2', name: 'Sarah Mitchell', email: 'sarah@arrowsgroups.com', role: 'Manager', status: 'active', lastLogin: '1 hr ago', avatar: 'SM' },
  { id: 'u3', name: 'James Okafor', email: 'james@arrowsgroups.com', role: 'Agent', status: 'active', lastLogin: '3 hrs ago', avatar: 'JO' },
  { id: 'u4', name: 'Priya Nair', email: 'priya@arrowsgroups.com', role: 'Developer', status: 'active', lastLogin: 'Yesterday', avatar: 'PN' },
  { id: 'u5', name: 'Carlos Vega', email: 'carlos@arrowsgroups.com', role: 'Agent', status: 'inactive', lastLogin: '5 days ago', avatar: 'CV' },
  { id: 'u6', name: 'Amara Diallo', email: 'amara@arrowsgroups.com', role: 'Analyst', status: 'pending', lastLogin: 'Never', avatar: 'AD' },
];

const mockRoles: Role[] = [
  {
    id: 'r1', name: 'Super Admin', description: 'Full platform access — all modules, settings, and user management', color: 'bg-red-500/15 text-red-400 border-red-500/20',
    permissions: ['dashboard', 'clients', 'leads', 'projects', 'bpo', 'voip', 'marketing', 'webdev', 'tickets', 'employees', 'invoices', 'reports', 'settings', 'user_management'],
    userCount: 1,
  },
  {
    id: 'r2', name: 'Manager', description: 'Operational oversight — view and manage all business modules', color: 'bg-primary/15 text-primary border-primary/20',
    permissions: ['dashboard', 'clients', 'leads', 'projects', 'bpo', 'tickets', 'employees', 'invoices', 'reports'],
    userCount: 2,
  },
  {
    id: 'r3', name: 'Agent', description: 'Frontline operations — tickets, BPO, and assigned tasks only', color: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
    permissions: ['dashboard', 'tickets', 'bpo', 'employee-portal'],
    userCount: 8,
  },
  {
    id: 'r4', name: 'Developer', description: 'Technical team — projects, web development, and code deployments', color: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/20',
    permissions: ['dashboard', 'projects', 'webdev', 'employee-portal'],
    userCount: 3,
  },
  {
    id: 'r5', name: 'Analyst', description: 'Data and reporting — read-only access to analytics and reports', color: 'bg-success/15 text-success border-success/20',
    permissions: ['dashboard', 'reports', 'leads'],
    userCount: 2,
  },
];

const allPermissions = [
  { key: 'dashboard', label: 'Dashboard', group: 'Core' },
  { key: 'clients', label: 'Clients', group: 'Core' },
  { key: 'leads', label: 'Leads / CRM', group: 'Core' },
  { key: 'projects', label: 'Projects', group: 'Operations' },
  { key: 'bpo', label: 'BPO Center', group: 'Operations' },
  { key: 'voip', label: 'VOIP / Calls', group: 'Operations' },
  { key: 'marketing', label: 'Digital Marketing', group: 'Operations' },
  { key: 'webdev', label: 'Web Development', group: 'Operations' },
  { key: 'tickets', label: 'Support Tickets', group: 'Support' },
  { key: 'employee-portal', label: 'Employee Portal', group: 'HR' },
  { key: 'employees', label: 'Employees', group: 'HR' },
  { key: 'invoices', label: 'Invoices & Payments', group: 'Finance' },
  { key: 'reports', label: 'Reports & Analytics', group: 'Finance' },
  { key: 'settings', label: 'Settings', group: 'Admin' },
  { key: 'user_management', label: 'User Management', group: 'Admin' },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function Toggle({ enabled, onToggle }: { enabled: boolean; onToggle: () => void }) {
  return (
    <button onClick={onToggle} className="flex-shrink-0">
      {enabled
        ? <ToggleRight size={28} className="text-primary" />
        : <ToggleLeft size={28} className="text-muted-foreground" />}
    </button>
  );
}

function StatusBadge({ status }: { status: User['status'] }) {
  const map = {
    active: 'bg-success/15 text-success',
    inactive: 'bg-muted text-muted-foreground',
    pending: 'bg-amber-500/15 text-amber-400',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${map[status]}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

// ─── Tab: Platform Configuration ─────────────────────────────────────────────

function PlatformConfig() {
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({
    companyName: 'Arrows Groups',
    siteUrl: 'https://arrowsgroups.com',
    supportEmail: 'support@arrowsgroups.com',
    timezone: 'UTC-5 (Eastern Time)',
    dateFormat: 'MM/DD/YYYY',
    currency: 'USD ($)',
    language: 'English (US)',
    sessionTimeout: '30',
  });

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div className="space-y-6">
      {/* Company Info */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Globe size={16} className="text-primary" />
          <h3 className="font-semibold text-foreground">Company Information</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { label: 'Company Name', key: 'companyName' },
            { label: 'Site URL', key: 'siteUrl' },
            { label: 'Support Email', key: 'supportEmail' },
          ].map(({ label, key }) => (
            <div key={key} className={key === 'supportEmail' ? 'md:col-span-2' : ''}>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">{label}</label>
              <input
                type="text"
                value={form[key as keyof typeof form]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Locale & Format */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Clock size={16} className="text-primary" />
          <h3 className="font-semibold text-foreground">Locale & Formatting</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { label: 'Timezone', key: 'timezone', options: ['UTC-5 (Eastern Time)', 'UTC-6 (Central Time)', 'UTC-7 (Mountain Time)', 'UTC-8 (Pacific Time)', 'UTC+0 (GMT)', 'UTC+1 (CET)'] },
            { label: 'Date Format', key: 'dateFormat', options: ['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'] },
            { label: 'Currency', key: 'currency', options: ['USD ($)', 'EUR (€)', 'GBP (£)', 'CAD (C$)'] },
            { label: 'Language', key: 'language', options: ['English (US)', 'English (UK)', 'Spanish', 'French'] },
          ].map(({ label, key, options }) => (
            <div key={key}>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">{label}</label>
              <div className="relative">
                <select
                  value={form[key as keyof typeof form]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="w-full appearance-none bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary pr-8"
                >
                  {options.map((o) => <option key={o}>{o}</option>)}
                </select>
                <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Security */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Lock size={16} className="text-primary" />
          <h3 className="font-semibold text-foreground">Security Settings</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Session Timeout (minutes)</label>
            <input
              type="number"
              value={form.sessionTimeout}
              onChange={(e) => setForm({ ...form, sessionTimeout: e.target.value })}
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-colors ${saved ? 'bg-success text-white' : 'bg-primary text-primary-foreground hover:bg-primary/90'}`}
        >
          {saved ? <><Check size={15} /> Saved</> : <><Save size={15} /> Save Changes</>}
        </button>
      </div>
    </div>
  );
}

// ─── Tab: User Management ─────────────────────────────────────────────────────

function UserManagement() {
  const [users, setUsers] = useState<User[]>(mockUsers);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');

  const filtered = users.filter((u) => {
    const matchSearch = u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === 'All' || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  const toggleStatus = (id: string) => {
    setUsers((prev) =>
      prev.map((u) => u.id === id ? { ...u, status: u.status === 'active' ? 'inactive' : 'active' } : u)
    );
  };

  const roleOptions = ['All', ...Array.from(new Set(users.map((u) => u.role)))];

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Users', value: users.length, color: 'text-primary', bg: 'bg-primary/10' },
          { label: 'Active', value: users.filter((u) => u.status === 'active').length, color: 'text-success', bg: 'bg-success/10' },
          { label: 'Pending', value: users.filter((u) => u.status === 'pending').length, color: 'text-amber-400', bg: 'bg-amber-400/10' },
        ].map((s) => (
          <div key={s.label} className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center`}>
              <span className={`text-lg font-bold ${s.color}`}>{s.value}</span>
            </div>
            <span className="text-sm text-muted-foreground">{s.label}</span>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <div className="relative">
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="appearance-none bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary pr-8"
          >
            {roleOptions.map((r) => <option key={r}>{r}</option>)}
          </select>
          <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        </div>
        <button className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
          <Plus size={15} />
          Invite User
        </button>
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">User</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Role</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Last Login</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((user) => (
                <tr key={user.id} className="hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-semibold text-primary">{user.avatar}</span>
                      </div>
                      <div>
                        <p className="font-medium text-foreground text-sm">{user.name}</p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-foreground">{user.role}</span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={user.status} />
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{user.lastLogin}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => toggleStatus(user.id)}
                        className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                        title={user.status === 'active' ? 'Deactivate' : 'Activate'}
                      >
                        {user.status === 'active' ? <X size={14} /> : <Check size={14} />}
                      </button>
                      <button className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
                        <Edit2 size={14} />
                      </button>
                      <button className="p-1.5 rounded-md hover:bg-danger/10 transition-colors text-muted-foreground hover:text-danger">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="py-10 text-center text-muted-foreground text-sm">No users match your search.</div>
        )}
      </div>
    </div>
  );
}

// ─── Tab: Role Permissions ────────────────────────────────────────────────────

function RolePermissions() {
  const [roles, setRoles] = useState<Role[]>(mockRoles);
  const [selectedRole, setSelectedRole] = useState<Role>(mockRoles[0]);
  const [saved, setSaved] = useState(false);

  const togglePermission = (permKey: string) => {
    const updated = selectedRole.permissions.includes(permKey)
      ? selectedRole.permissions.filter((p) => p !== permKey)
      : [...selectedRole.permissions, permKey];
    const updatedRole = { ...selectedRole, permissions: updated };
    setSelectedRole(updatedRole);
    setRoles((prev) => prev.map((r) => r.id === updatedRole.id ? updatedRole : r));
  };

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const permGroups = Array.from(new Set(allPermissions.map((p) => p.group)));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      {/* Role List */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Roles</p>
        {roles.map((role) => (
          <button
            key={role.id}
            onClick={() => setSelectedRole(role)}
            className={`w-full text-left p-3 rounded-xl border transition-colors ${selectedRole.id === role.id ? 'border-primary bg-primary/5' : 'border-border bg-card hover:bg-muted/30'}`}
          >
            <div className="flex items-center justify-between mb-1">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${role.color}`}>{role.name}</span>
              <span className="text-xs text-muted-foreground">{role.userCount} users</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed mt-1">{role.description}</p>
          </button>
        ))}
      </div>

      {/* Permissions Editor */}
      <div className="lg:col-span-2 bg-card border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="font-semibold text-foreground">{selectedRole.name} — Permissions</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{selectedRole.permissions.length} of {allPermissions.length} modules enabled</p>
          </div>
          <button
            onClick={handleSave}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${saved ? 'bg-success text-white' : 'bg-primary text-primary-foreground hover:bg-primary/90'}`}
          >
            {saved ? <><Check size={14} /> Saved</> : <><Save size={14} /> Save</>}
          </button>
        </div>

        <div className="space-y-5">
          {permGroups.map((group) => (
            <div key={group}>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{group}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {allPermissions.filter((p) => p.group === group).map((perm) => {
                  const enabled = selectedRole.permissions.includes(perm.key);
                  const isLocked = selectedRole.name === 'Super Admin';
                  return (
                    <div
                      key={perm.key}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${enabled ? 'border-primary/30 bg-primary/5' : 'border-border bg-background'}`}
                    >
                      <span className="text-sm text-foreground">{perm.label}</span>
                      {isLocked
                        ? <Check size={16} className="text-primary flex-shrink-0" />
                        : (
                          <button
                            onClick={() => togglePermission(perm.key)}
                            className={`w-9 h-5 rounded-full transition-colors relative flex-shrink-0 ${enabled ? 'bg-primary' : 'bg-muted'}`}
                          >
                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                          </button>
                        )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Tab: System Preferences ──────────────────────────────────────────────────

function SystemPreferences() {
  const [prefs, setPrefs] = useState({
    emailNotifications: true,
    smsAlerts: false,
    slackIntegration: false,
    autoBackup: true,
    maintenanceMode: false,
    debugLogging: false,
    twoFactorRequired: true,
    auditLog: true,
    clientPortalEnabled: true,
    employeePortalEnabled: true,
    publicSiteEnabled: true,
    invoiceAutoSend: true,
    leadAutoAssign: false,
    ticketAutoEscalate: true,
  });

  const [saved, setSaved] = useState(false);

  const toggle = (key: keyof typeof prefs) => setPrefs((p) => ({ ...p, [key]: !p[key] }));

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const sections = [
    {
      title: 'Notifications', icon: Bell,
      items: [
        { key: 'emailNotifications', label: 'Email Notifications', desc: 'Send system alerts and updates via email' },
        { key: 'smsAlerts', label: 'SMS Alerts', desc: 'Critical alerts sent via SMS to admins' },
        { key: 'slackIntegration', label: 'Slack Integration', desc: 'Post notifications to Slack channels' },
      ],
    },
    {
      title: 'Security & Compliance', icon: Shield,
      items: [
        { key: 'twoFactorRequired', label: 'Require 2FA', desc: 'Enforce two-factor authentication for all users' },
        { key: 'auditLog', label: 'Audit Logging', desc: 'Log all admin actions for compliance review' },
        { key: 'debugLogging', label: 'Debug Logging', desc: 'Enable verbose logs (not recommended in production)' },
      ],
    },
    {
      title: 'Data & Backup', icon: Database,
      items: [
        { key: 'autoBackup', label: 'Automatic Backups', desc: 'Daily automated database backups' },
        { key: 'maintenanceMode', label: 'Maintenance Mode', desc: 'Take the platform offline for maintenance' },
      ],
    },
    {
      title: 'Portal Access', icon: Globe,
      items: [
        { key: 'clientPortalEnabled', label: 'Client Portal', desc: 'Allow clients to access their portal' },
        { key: 'employeePortalEnabled', label: 'Employee Portal', desc: 'Allow employees to access their workspace' },
        { key: 'publicSiteEnabled', label: 'Public Site', desc: 'Arrows Groups public-facing website' },
      ],
    },
    {
      title: 'Automation', icon: Sliders,
      items: [
        { key: 'invoiceAutoSend', label: 'Auto-Send Invoices', desc: 'Automatically email invoices when generated' },
        { key: 'leadAutoAssign', label: 'Auto-Assign Leads', desc: 'Round-robin lead assignment to sales agents' },
        { key: 'ticketAutoEscalate', label: 'Auto-Escalate Tickets', desc: 'Escalate unresolved tickets after 24 hours' },
      ],
    },
  ];

  return (
    <div className="space-y-5">
      {prefs.maintenanceMode && (
        <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3">
          <AlertCircle size={16} className="text-amber-400 flex-shrink-0" />
          <p className="text-sm text-amber-400">Maintenance mode is active — the platform is currently offline for end users.</p>
        </div>
      )}

      {sections.map(({ title, icon: Icon, items }) => (
        <div key={title} className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Icon size={16} className="text-primary" />
            <h3 className="font-semibold text-foreground">{title}</h3>
          </div>
          <div className="space-y-3">
            {items.map(({ key, label, desc }) => (
              <div key={key} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <div>
                  <p className="text-sm font-medium text-foreground">{label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
                </div>
                <Toggle enabled={prefs[key as keyof typeof prefs]} onToggle={() => toggle(key as keyof typeof prefs)} />
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-colors ${saved ? 'bg-success text-white' : 'bg-primary text-primary-foreground hover:bg-primary/90'}`}
        >
          {saved ? <><Check size={15} /> Saved</> : <><Save size={15} /> Save Preferences</>}
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

type Tab = 'platform' | 'users' | 'roles' | 'preferences';

const tabs: { key: Tab; label: string; icon: React.ElementType }[] = [
  { key: 'platform', label: 'Platform Config', icon: Globe },
  { key: 'users', label: 'User Management', icon: Users },
  { key: 'roles', label: 'Role Permissions', icon: Shield },
  { key: 'preferences', label: 'System Preferences', icon: Sliders },
];

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('platform');

  return (
    <AppLayout>
      <div className="min-h-screen bg-background px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-foreground">Admin Settings</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Platform configuration, user management, and system preferences</p>
          </div>
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Settings size={18} className="text-primary" />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-card border border-border rounded-xl p-1 mb-6 overflow-x-auto scrollbar-thin">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                activeTab === key
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Icon size={14} />
              <span className="hidden xs:inline sm:inline">{label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'platform' && <PlatformConfig />}
        {activeTab === 'users' && <UserManagement />}
        {activeTab === 'roles' && <RolePermissions />}
        {activeTab === 'preferences' && <SystemPreferences />}
      </div>
    </AppLayout>
  );
}
